// src/components/Pdfs/PdfRelacionCerrada.js
// Genera el PDF de una RelacionGastos (cerrada o en borrador) en el cliente,
// usando jsPDF + jspdf-autotable. Mantiene la linea visual SIG BIOLAB
// definida en PdfActa.js: encabezado teal [34,171,180], logo, pie de firmas,
// paginacion. Incluye un bloque destacado "Estado del bolsillo despues del
// cierre" con el saldoFinal en verde (>=0) o rojo (<0).
//
// Uso: PdfRelacionCerrada({ relacion, gastos, entregas, traslados, comprobantes })
// Todo el dataset debe llegar ya hidratado (no hace fetch).

import autoTable from 'jspdf-autotable';
import { jsPDF } from 'jspdf';
import moment from 'moment';
import 'moment/locale/es';
import { logoBiolab } from '../../helpers/imagen/Imagen';
import { formatoFechaColombia } from '../../utils/fechasColombia';

moment().locale('es');

const formatoCOP = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const HEADER_ROWS = (consecutivo) => [
  [
    { content: 'SISTEMA INTEGRADO DE GESTIÓN', colSpan: 3, rowSpan: 1,
      styles: { halign: 'center', cellWidth: 6, fillColor: [34, 171, 180] } }
  ],
  [
    { content: '', colSpan: 1, rowSpan: 2 },
    { content: 'Proceso: Gestión Financiera', styles: { halign: 'center' } },
    'Código: APO-GAF-FOR-009'
  ],
  [
    { content: 'Formato: Relación de gastos', styles: { halign: 'center' } },
    'Versión: 01'
  ]
];

export const PdfRelacionCerrada = ({
  relacion,
  gastos = [],
  entregas = [],
  traslados = [],
  comprobantes = [],
  firmaResponsable = null,
  firmaAprobador = null
}) => {
  if (!relacion) {
    console.warn('PdfRelacionCerrada: relacion vacia, abortando.');
    return;
  }

  const consecutivo = relacion.consecutivo || 'relacion';
  const bolsillo = relacion.bolsillo || {};
  const responsable = relacion.responsable || {};
  const aprobador = relacion.aprobadoPor || null;
  const aprobada = !!(aprobador && relacion.fechaAprobacion);

  const doc = new jsPDF({
    unit: 'cm',
    orientation: 'p',
    format: 'A4'
  });

  const dibujarHeaderYFooter = (data) => {
    // Encabezado SIG.
    autoTable(doc, {
      startY: 0.5,
      showHead: 'everyPage',
      headStyles: { fontStyle: 'bold', fontSize: 6 },
      styles: { lineColor: 10, lineWidth: 0.02 },
      theme: 'plain',
      head: HEADER_ROWS(consecutivo),
      didDrawCell: (d) => {
        if (d.section === 'head' && d.column.index === 0 && d.row.index === 1) {
          doc.addImage(logoBiolab, 'JPEG', d.cell.x + 1.2, d.cell.y + 0.1, 2.5, 0.8);
        }
      }
    });

    // Pie de pagina con firmas y paginacion.
    autoTable(doc, {
      startY: 27.5,
      footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      styles: { lineColor: 10, lineWidth: 0.02, fontSize: 8 },
      foot: [['Elaboró: Director de calidad', 'Revisó: Director de Laboratorio', 'Aprobó: Gerente']]
    });
    const pageStr = `Página ${doc.internal.getNumberOfPages()}`;
    doc.setFontSize(8);
    doc.text(pageStr, data.settings.margin.right, 28.5);
  };

  // ---- Cabecera del documento ----
  doc.setFontSize(10);
  autoTable(doc, {
    startY: 3,
    showHead: 'everyPage',
    headStyles: { fontStyle: 'bold', fontSize: 8, halign: 'center' },
    theme: 'plain',
    head: [[
      { content: '', styles: { cellWidth: 12 } },
      { content: 'Consecutivo:', styles: { fillColor: [34, 171, 180], lineColor: 10, lineWidth: 0.02 } },
      { content: consecutivo, styles: { lineColor: 10, lineWidth: 0.02 } }
    ]]
  });

  const nombreResponsableCabecera =
    `${responsable.nombre || ''} ${responsable.apellido || ''}`.trim() || '—';

  doc.text(`Cereté / ${formatoFechaColombia(relacion.createdAt || relacion.fechaCierre)}`, 1.5, 3.5);
  doc.text(`Bolsillo: ${bolsillo.codigo || ''} - ${bolsillo.nombre || '—'}`, 1.5, 4.5);
  doc.text(`Responsable: ${nombreResponsableCabecera}`, 1.5, 5.0);
  doc.text(`Meses cubiertos: ${(relacion.mesesRepresentados || []).join(', ') || '—'}`, 1.5, 6.0);
  doc.text(
    `Período: ${formatoFechaColombia(relacion.fechaInicio)} - ${formatoFechaColombia(relacion.fechaCierre)}`,
    1.5, 6.5
  );

  // ---- Seccion 1: Gastos relacionados ----
  const gastosBody = gastos.map((g, idx) => [
    idx + 1,
    formatoFechaColombia(g.fecha),
    g.categoria || '—',
    g.descripcion || '—',
    g.numeroFactura || (g.tieneFactura ? 'sí' : '—'),
    g.proveedor?.nombre || g.proveedorSinCrear?.nombre || '—',
    g.ordenTrabajo?.numeroOT || '—',
    formatoCOP(g.valorTotal),
    formatoCOP(g.iva || 0)
  ]);

  const totalGastos = gastos.reduce((s, g) => s + (g.valorTotal || 0), 0);
  const totalIVA = gastos.reduce((s, g) => s + (g.iva || 0), 0);

  autoTable(doc, {
    startY: 8,
    theme: 'plain',
    headStyles: { fontStyle: 'bold', fillColor: [34, 171, 180], textColor: [0, 0, 0] },
    showHead: 'everyPage',
    showFoot: 'lastPage',
    margin: { top: 3, bottom: 2.7 },
    styles: { lineColor: 10, lineWidth: 0.01, fontSize: 7, halign: 'center' },
    head: [
      [{ content: '1. GASTOS RELACIONADOS', colSpan: 9 }],
      ['No.', 
        {content:'Fecha', styles: { cellWidth: 2 } }, 
        'Categoría', 'Descripción', 'Factura',
        {content:'Tercero', styles: { cellWidth: 3 }}, 'OT', 'Valor', 'IVA']
    ],
    body: gastosBody.length > 0 ? gastosBody : [[{
      content: 'Sin gastos en este cierre.',
      colSpan: 9,
      styles: { halign: 'center', fontStyle: 'italic', textColor: [108, 117, 125] }
    }]],
    foot: [[
      { content: 'Totales', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold', fillColor: [222, 226, 230] } },
      { content: formatoCOP(totalGastos), styles: { fontStyle: 'bold', fillColor: [222, 226, 230] } },
      { content: formatoCOP(totalIVA), styles: { fontStyle: 'bold', fillColor: [222, 226, 230] } }
    ]],
    didDrawPage: dibujarHeaderYFooter
  });

  // ---- Seccion 2: Entregas recibidas ----
  const totalEntregas = entregas.reduce((s, e) => s + (e.monto || 0), 0);
  autoTable(doc, {
    theme: 'plain',
    headStyles: { fontStyle: 'bold', fillColor: [34, 171, 180], textColor: [0, 0, 0] },
    margin: { top: 3, bottom: 2.7 },
    styles: { lineColor: 10, lineWidth: 0.01, fontSize: 8, halign: 'center' },
    head: [
      [{ content: '2. ENTREGAS RECIBIDAS', colSpan: 5 }],
      ['Fecha', 'Consecutivo', 'Origen', 'Descripción', 'Monto']
    ],
    body: entregas.length > 0
      ? entregas.map((e) => [
          formatoFechaColombia(e.fecha),
          e.consecutivo || '—',
          e.cuentaOrigen?.nombre || '—',
          e.descripcion || '—',
          formatoCOP(e.monto)
        ])
      : [[{
          content: 'Sin entregas en este cierre.',
          colSpan: 5,
          styles: { halign: 'center', fontStyle: 'italic', textColor: [108, 117, 125] }
        }]],
    foot: [[
      { content: 'Total entregas', colSpan: 4, styles: { halign: 'right', fontStyle: 'bold', fillColor: [222, 226, 230] } },
      { content: formatoCOP(totalEntregas), styles: { fontStyle: 'bold', fillColor: [222, 226, 230] } }
    ]],
    didDrawPage: dibujarHeaderYFooter
  });

  // ---- Seccion 3: Traslados ----
  const bolsilloId = String(relacion.bolsillo?._id || relacion.bolsillo || '');
  const trasladosBody = traslados.map((t) => {
    const destinoId = String(t.cuenta_destino?._id || t.cuenta_destino || '');
    const esRecibido = destinoId === bolsilloId;
    return [
      formatoFechaColombia(t.fecha),
      esRecibido ? 'Recibido' : 'Enviado',
      t.consecutivo || '—',
      esRecibido ? (t.cuenta_origen?.nombre || '—') : (t.cuenta_destino?.nombre || '—'),
      t.descripcion || '—',
      `${esRecibido ? '+' : '-'}${formatoCOP(t.monto)}`
    ];
  });

  autoTable(doc, {
    theme: 'plain',
    headStyles: { fontStyle: 'bold', fillColor: [34, 171, 180], textColor: [0, 0, 0] },
    margin: { top: 3, bottom: 2.7 },
    styles: { lineColor: 10, lineWidth: 0.01, fontSize: 8, halign: 'center' },
    head: [
      [{ content: '3. TRASLADOS', colSpan: 6 }],
      ['Fecha', 'Tipo', 'Consecutivo', 'Contraparte', 'Descripción', 'Monto']
    ],
    body: trasladosBody.length > 0 ? trasladosBody : [[{
      content: 'Sin traslados en este cierre.',
      colSpan: 6,
      styles: { halign: 'center', fontStyle: 'italic', textColor: [108, 117, 125] }
    }]],
    didDrawPage: dibujarHeaderYFooter
  });

  // ---- Seccion 4: Estado del bolsillo despues del cierre ----
  const saldoFinal = Number(relacion.saldoFinal || 0);
  const colorSaldoFinal = saldoFinal >= 0 ? [25, 135, 84] : [220, 53, 69]; // verde / rojo

  autoTable(doc, {
    theme: 'plain',
    headStyles: { fontStyle: 'bold', fillColor: [34, 171, 180], textColor: [0, 0, 0], halign: 'center' },
    margin: { top: 3, bottom: 2.7, left: 1.5, right: 1.5 },
    styles: { lineColor: 10, lineWidth: 0.01, fontSize: 9, cellPadding: { top: 0.15, right: 0.3, bottom: 0.15, left: 0.3 } },
    // tableWidth 'auto' (default) hace que la tabla ocupe el ancho disponible
    // entre los margenes left/right. Sin cellWidth fijo en columnStyles, las
    // columnas se reparten proporcional al contenido. Usamos guion ASCII '-'
    // en vez del minus mathematical U+2212 para evitar problemas de glifo en
    // jsPDF (que puede aplicar letter-spacing forzado cuando no encuentra el
    // caracter en la fuente base).
    head: [[{ content: '4. ESTADO DEL BOLSILLO DESPUÉS DEL CIERRE', colSpan: 2 }]],
    body: [
      ['Saldo inicial del periodo:', formatoCOP(relacion.saldoInicial)],
      ['(+) Entregas recibidas:', formatoCOP(relacion.montoTotalEntregasRecibidas)],
      ['(+) Traslados recibidos:', formatoCOP(relacion.montoTotalTrasladosRecibidos)],
      ['(-) Traslados enviados:', formatoCOP(relacion.montoTotalTrasladosEnviados)],
      ['(-) Gastos relacionados:', formatoCOP(relacion.montoTotalGastos)],
      [
        { content: 'SALDO FINAL DEL BOLSILLO', styles: { fontStyle: 'bold', fillColor: [248, 249, 250] } },
        { content: formatoCOP(saldoFinal),
          styles: {
            fontStyle: 'bold',
            fontSize: 11,
            textColor: colorSaldoFinal,
            fillColor: [248, 249, 250]
          } }
      ]
    ],
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'right' }
    },
    didDrawPage: dibujarHeaderYFooter
  });

  // ---- Seccion 5: Espacio de firmas ----
  // Estructura: dos columnas grandes (Elabora / Aprueba). Cada celda tiene
  // espacio vertical para la firma (addImage via didDrawCell) + el nombre y
  // la fecha debajo. Si la relacion no ha sido aprobada, la columna del
  // aprobador queda en blanco con la nota 'Pendiente de aprobacion'.
  const nombreResponsable = `${responsable.nombre || ''} ${responsable.apellido || ''}`.trim() || '—';
  const nombreAprobador   = aprobada
    ? (`${aprobador.nombre || ''} ${aprobador.apellido || ''}`.trim() || '—')
    : 'Pendiente de aprobación';
  const fechaResponsable  = formatoFechaColombia(relacion.fechaCierre || relacion.createdAt);
  const fechaAprobador    = aprobada
    ? formatoFechaColombia(relacion.fechaAprobacion)
    : '—';

  autoTable(doc, {
    theme: 'plain',
    headStyles: { fontStyle: 'bold', fillColor: [34, 171, 180], textColor: [0, 0, 0], halign: 'center' },
    margin: { top: 3, bottom: 2.7, left: 1.5, right: 1.5 },
    styles: { lineColor: 10, lineWidth: 0.01, fontSize: 8, halign: 'center', valign: 'middle', cellPadding: 0.2 },
    pageBreak: 'avoid',
    head: [[
      { content: 'Elaboró (responsable)' },
      { content: aprobada ? 'Aprobó' : 'Aprobó (pendiente)' }
    ]],
    body: [
      // Fila 1: espacio para la firma (se pinta con didDrawCell). minCellHeight
      // garantiza el alto suficiente para la imagen base64.
      [
        { content: ' ', styles: { minCellHeight: 2.5, fillColor: [255, 255, 255] } },
        { content: aprobada ? ' ' : ' ', styles: { minCellHeight: 2.5, fillColor: [255, 255, 255] } }
      ],
      // Fila 2: nombre.
      [
        { content: nombreResponsable, styles: { fontStyle: 'bold' } },
        { content: nombreAprobador, styles: { fontStyle: aprobada ? 'bold' : 'italic',
          textColor: aprobada ? [0, 0, 0] : [108, 117, 125] } }
      ],
      // Fila 3: fecha.
      [
        { content: `Fecha: ${fechaResponsable}`, styles: { fontSize: 7, textColor: [73, 80, 87] } },
        { content: `Fecha: ${fechaAprobador}`,   styles: { fontSize: 7, textColor: [73, 80, 87] } }
      ]
    ],
    didDrawCell: (data) => {
      // Solo dibujar firma en la primera fila del body (espacio vertical).
      if (data.section !== 'body' || data.row.index !== 0) return;
      const colIndex = data.column.index;
      const imgBase64 = colIndex === 0
        ? firmaResponsable
        : (aprobada ? firmaAprobador : null);
      if (!imgBase64) return;
      try {
        // Centrar la firma dentro de la celda. Ancho 4 cm, alto 1.8 cm.
        const imgWidth = 4;
        const imgHeight = 1.8;
        const x = data.cell.x + (data.cell.width - imgWidth) / 2;
        const y = data.cell.y + (data.cell.height - imgHeight) / 2;
        const formato = imgBase64.startsWith('data:image/png') ? 'PNG' : 'JPEG';
        doc.addImage(imgBase64, formato, x, y, imgWidth, imgHeight);
      } catch (err) {
        console.warn('[PdfRelacionCerrada] No se pudo pintar firma:', err?.message);
      }
    },
    didDrawPage: dibujarHeaderYFooter
  });

  doc.save(`RelacionGastos-${consecutivo}.pdf`);
};

export default PdfRelacionCerrada;
